import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { Input } from "@/modern/components/ui/input";
import { Search } from "lucide-react";
import { TaskTab } from "./tabs/TaskTab";
import { ResourceTab } from "./tabs/ResourceTab";
import { ClientTab } from "./tabs/ClientTab";
import { ContactTab } from "./tabs/ContactTab";
import { UserTab } from "./tabs/UserTab";
import { BudgetTab } from "./tabs/BudgetTab";
import { ChecklistTab } from "./tabs/ChecklistTab";
import { EntityFinderProvider, useEntityFinderContext } from "./context/EntityFinderContext";

interface SelectedRecords {
  [storeKey: string]: any[];
}

interface EntityFinderEditorProps {
  record: {
    onEntitiesSelected?: (records: SelectedRecords) => void;
    isOnlyFinder?: boolean;
    [key: string]: any;
  };
  onClose: () => void;
  editorInstance?: any;
  context?: any; // eslint-disable-line @typescript-eslint/no-unused-vars
}

export const EntityFinderEditor = ({ record, onClose, editorInstance }: EntityFinderEditorProps) => {
  return (
    <EntityFinderProvider record={record} onClose={onClose}>
      <EntityFinderContent record={record} onClose={onClose} editorInstance={editorInstance} />
    </EntityFinderProvider>
  );
};

const EntityFinderContent = ({ record, onClose, editorInstance }: EntityFinderEditorProps) => {
  const { handleSearchChange, handleSearchClear, handleTabChange, searchValue, activeTab } = useEntityFinderContext();
  return (
    <BaseModal
      record={record}
      onClose={onClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "40rem",
        height: "40rem",
        maxHeight: "100vh",
        maxWidth: "100vw",
        minWidth: "40rem",
        minHeight: "40rem"
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      headerRenderer={() => (
        <div className="drag-handle flex items-center gap-2 px-4 py-3 bg-background">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input type="text" placeholder="Entity Finder" value={searchValue} onChange={handleSearchChange} className="pl-9 pr-16" autoFocus />
            {searchValue && (
              <button onClick={handleSearchClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>
          <button onClick={onClose} className="text-sm px-2 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent">
            ESC
          </button>
        </div>
      )}
    >
      <BaseModalBody className="!pt-0">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
          <TabsList className="sticky top-0 z-10 w-full rounded-none bg-background overflow-x-auto whitespace-nowrap gap-2 mb-3">
            <TabsTrigger value="tasks" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Tasks
            </TabsTrigger>
            <TabsTrigger value="resources" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Resources
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Clients
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Contacts
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Board Users
            </TabsTrigger>
            <TabsTrigger value="budgets" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Budgets
            </TabsTrigger>
            <TabsTrigger value="checklists" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Checklists
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="flex-1 mt-0 overflow-hidden">
            <TaskTab />
          </TabsContent>

          <TabsContent value="resources" className="flex-1 mt-0 overflow-hidden">
            <ResourceTab />
          </TabsContent>

          <TabsContent value="clients" className="flex-1 mt-0 overflow-hidden">
            <ClientTab />
          </TabsContent>

          <TabsContent value="contacts" className="flex-1 mt-0 overflow-hidden">
            <ContactTab />
          </TabsContent>

          <TabsContent value="users" className="flex-1 mt-0 overflow-hidden">
            <UserTab />
          </TabsContent>

          <TabsContent value="budgets" className="flex-1 mt-0 overflow-hidden">
            <BudgetTab />
          </TabsContent>

          <TabsContent value="checklists" className="flex-1 mt-0 overflow-hidden">
            <ChecklistTab />
          </TabsContent>
        </Tabs>
      </BaseModalBody>
    </BaseModal>
  );
};
